class BundleBuilder extends HTMLElement {
	connectedCallback() {
		this.fromCartItems = [];
		document.addEventListener('bundle:add', this.onAdd);
		document.addEventListener('bundle:variant-change', this.onVariantChange);
		this.addEventListener('bundle:item-changed', this.onItemChanged);
		this.submitButton?.addEventListener('click', this.onSubmit);
		this.initFromUrl();
	}

	disconnectedCallback() {
		document.removeEventListener('bundle:add', this.onAdd);
		document.removeEventListener('bundle:variant-change', this.onVariantChange);
		this.removeEventListener('bundle:item-changed', this.onItemChanged);
		this.submitButton?.removeEventListener('click', this.onSubmit);
	}

	get items() {
		return Array.from(this.querySelectorAll('bundle-item'));
	}

	get submitButton() {
		return this.querySelector('.bundle-builder__cta');
	}

	async initFromUrl() {
		const params = new URLSearchParams(window.location.search);
		const itemsParam = params.get('items');
		if (!itemsParam) return;

		const keys = itemsParam.split(',').filter(Boolean);
		if (!keys.length) return;

		try {
			const res = await fetch('/cart.js');
			const cart = await res.json();

			let lastVariantTitle = null;

			for (const key of keys) {
				const li = cart.items.find((i) => i.key === key);
				if (!li) continue;
				const modelName = li.product_title.split(' ')[0];
				const product = {
					productId: li.product_id,
					title: li.product_title,
					modelName,
					productType: li.product_type,
					image: li.image,
					variantId: li.variant_id,
					variantTitle: li.variant_title,
					fromCart: true,
					lineItemKey: li.key,
				};

				this.fromCartItems.push(product);

				const slot = this.items.find((item) => !item.checkbox?.checked);
				if (!slot) break;

				document.dispatchEvent(
					new CustomEvent('bundle:update', {
						detail: { bundleId: slot.bundleId, action: 'add', product },
					})
				);

				lastVariantTitle = li.variant_title;
			}

			if (lastVariantTitle) {
				this.preferredVariant = lastVariantTitle;
				this.querySelectorAll('bundle-product').forEach((bp) => {
					bp.applyPreferredVariant?.(lastVariantTitle);
				});
			}

			this.broadcastCounts();
		} catch {
			// Cart fetch failed — slots stay empty
		}

		const url = new URL(window.location);
		url.searchParams.delete('items');
		history.replaceState(null, '', url);
	}

	onVariantChange = (event) => {
		const { variantTitle } = event.detail;
		if (variantTitle) this.preferredVariant = variantTitle;
	};

	onAdd = (event) => {
		const { productId, variantId, variantTitle, maxQty, productTitle, modelName, productType, image } = event.detail;

		if (variantTitle) this.preferredVariant = variantTitle;

		const variantCount = this.items.filter((item) => item.variantId === variantId).length;
		if (maxQty > 0 && variantCount >= maxQty) return;

		const slot = this.items.find((item) => !item.checkbox?.checked);
		if (!slot) return;

		this.variantMaxQty = this.variantMaxQty || {};
		this.variantMaxQty[variantId] = maxQty;

		document.dispatchEvent(
			new CustomEvent('bundle:update', {
				detail: {
					bundleId: slot.bundleId,
					action: 'add',
					product: { productId, title: productTitle, modelName, productType, image, variantId, variantTitle },
				},
			})
		);
	};

	onItemChanged = () => {
		this.broadcastCounts();
		this.updateSubmitState();
	};

	updateSubmitState() {
		const button = this.submitButton;
		if (!button) return;
		const allRequired = this.items.every(
			(item) => !item.checkbox?.required || item.checkbox.checked
		);
		button.disabled = !allRequired;
	}

	onSubmit = async () => {
		const button = this.submitButton;
		if (!button || button.disabled) return;

		const selected = this.items.filter((item) => item.checkbox?.checked && item.variantId);
		if (!selected.length) return;

		button.disabled = true;
		button.classList.add('is-loading');

		const toAdd = selected.filter((item) => !item.fromCart);
		const activeCartKeys = new Set(selected.filter((item) => item.fromCart).map((item) => item.lineItemKey));
		const toRemove = this.fromCartItems.filter((p) => !activeCartKeys.has(p.lineItemKey));

		try {
			for (const item of toRemove) {
				await fetch('/cart/change.js', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ id: item.lineItemKey, quantity: 0 }),
				});
			}

			if (toAdd.length) {
				await fetch('/cart/add.js', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						items: toAdd.map((item) => ({ id: item.variantId, quantity: 1, properties: { _source: 'bundle-builder' } })),
					}),
				});
			}

			window.location.href = '/checkout';
		} catch {
			button.disabled = false;
			button.classList.remove('is-loading');
		}
	};

	broadcastCounts() {
		const counts = {};
		const variantCounts = {};
		for (const item of this.items) {
			if (item.checkbox?.checked && item.productId) {
				counts[item.productId] = (counts[item.productId] || 0) + 1;
			}
			if (item.checkbox?.checked && item.variantId) {
				variantCounts[item.variantId] = (variantCounts[item.variantId] || 0) + 1;
			}
		}
		const full = this.items.every((item) => item.checkbox?.checked);

		this.bundleCounts = counts;
		this.bundleFull = full;

		document.dispatchEvent(
			new CustomEvent('bundle:counts-updated', {
				detail: { counts, variantCounts, variantMaxQty: this.variantMaxQty || {}, full },
			})
		);
	}
}

customElements.define('bundle-builder', BundleBuilder);
